import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #lifecycleToken = 0;
  #animationFallback;
  #scrollY = 0;
  #focusOrigin;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onClick, true);
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onClick, true);
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#clearAnimationFallback();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #onClick = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.some((node) => node instanceof Element && node.matches?.('dialog'))) return;
    const account = path.find(
      (node) => node instanceof Element && node.matches?.('shopify-account.scc-menu-drawer__account-action'),
    );

    if (account) {
      const sharedAccount = [...document.querySelectorAll('shopify-account:not(.scc-menu-drawer__account-action)')].find(
        (candidate) => !this.contains(candidate),
      );
      if (!sharedAccount) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#closeOpenAccountDialogs();
      this.#forceClosed();
      const sharedTrigger = sharedAccount.shadowRoot?.querySelector('button[aria-label="Account"]') ?? sharedAccount;
      sharedTrigger.click();
      return;
    }

    if (!(event.target instanceof Element) || event.defaultPrevented) return;
    const link = event.target.closest('a[href]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self' || link.hasAttribute('download')) return;
    this.#forceClosed();
  };

  #onPageShow = () => {
    this.#closeOpenAccountDialogs();
    this.#forceClosed();
  };

  #onPageHide = () => {
    this.#closeOpenAccountDialogs();
    this.#forceClosed();
  };

  #closeOpenAccountDialogs() {
    document.querySelectorAll('shopify-account').forEach((account) => {
      const dialog = account.shadowRoot?.querySelector('dialog[open]');
      const closeButton = dialog?.querySelector('#close-button, [aria-label="Close menu"]');
      closeButton?.click();
    });
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const lifecycleToken = ++this.#lifecycleToken;
    this.#clearAnimationFallback();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#scrollY = window.scrollY;
      this.#focusOrigin = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    // The native <details> state controls the backdrop immediately. Keep the
    // drawer in the same visible state before the next frame can be delayed.
    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    requestAnimationFrame(() => {
      if (lifecycleToken !== this.#lifecycleToken || !this.isOpen) return;
      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      this.#afterAnimation(drawer || details, () => {
        if (lifecycleToken === this.#lifecycleToken && this.isOpen) trapFocus(details);
      }, lifecycleToken);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const lifecycleToken = ++this.#lifecycleToken;
    this.#clearAnimationFallback();
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    this.#afterAnimation(drawer || details, () => {
        if (lifecycleToken !== this.#lifecycleToken) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#restoreFocusAndScroll();
        } else {
          trapFocus(this.refs.details);
        }
      }, lifecycleToken);
  }

  #afterAnimation(element, callback, lifecycleToken) {
    let completed = false;
    const finish = () => {
      if (completed || lifecycleToken !== this.#lifecycleToken) return;
      completed = true;
      this.#clearAnimationFallback();
      callback();
    };

    this.#animationFallback = window.setTimeout(finish, 500);
    onAnimationEnd(element, finish, { subtree: false });
  }

  #clearAnimationFallback() {
    if (this.#animationFallback) {
      window.clearTimeout(this.#animationFallback);
      this.#animationFallback = undefined;
    }
  }

  #forceClosed() {
    const wasActive = this.isOpen || this.refs.details.classList.contains('menu-open') || this.querySelector('details[open]');
    this.#lifecycleToken += 1;
    this.#clearAnimationFallback();
    reset(this.refs.details);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]').forEach(reset);
    if (wasActive) {
      removeTrapFocus();
      this.#restoreFocusAndScroll();
    }
  }

  #restoreFocusAndScroll() {
    const focusOrigin = this.#focusOrigin;
    this.#focusOrigin = undefined;
    if (focusOrigin?.isConnected && typeof focusOrigin.focus === 'function') {
      focusOrigin.focus({ preventScroll: true });
    }
    window.scrollTo({ top: this.#scrollY, left: 0, behavior: 'instant' });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
